import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';

const DAY_START_HOUR = 6;   // 06:00
const DAY_END_HOUR = 18;    // 18:00

const ConsumptionGraphTab = () => {
    const { meterId } = useParams();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [graphData, setGraphData] = useState([]);
    const [loading, setLoading] = useState(false);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    useEffect(() => {
        if (meterId && selectedFacilityId) {
            fetchGraphData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meterId, selectedFacilityId]);

    const formatApiDate = (date) => {
        const d = new Date(date);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    // The raw `date` field on readings comes back as e.g. "9/23/2026" —
    // compare by calendar day rather than trusting string format.
    const isSameDay = (rawDate, target) => {
        if (!rawDate) return false;
        const a = new Date(rawDate);
        return (
            a.getFullYear() === target.getFullYear() &&
            a.getMonth() === target.getMonth() &&
            a.getDate() === target.getDate()
        );
    };

    const timeToMinutes = (time) => {
        const [h, m] = (time || '00:00').split(':').map(Number);
        return h * 60 + (m || 0);
    };

    // Buckets one day's raw readings into 24 hourly consumption values, then
    // splits those into a Day series and a Night series on the same hour axis.
    const buildHourlySeries = (readings, targetDate) => {
        const dayReadings = readings
            .filter((r) => isSameDay(r.date, targetDate))
            .map((r) => ({ ...r, hour: Math.floor(timeToMinutes(r.time) / 60) }))
            .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

        // Carry the last known cumulative reading forward into every hour
        const hourlyCumulative = new Array(24).fill(null);
        let lastKnown = null;
        let idx = 0;
        for (let h = 0; h < 24; h++) {
            while (idx < dayReadings.length && dayReadings[idx].hour <= h) {
                lastKnown = dayReadings[idx].reading;
                idx++;
            }
            hourlyCumulative[h] = lastKnown;
        }

        // Consumption for hour h = cumulative[h] - cumulative[h-1]
        const hourlyConsumption = hourlyCumulative.map((val, i) => {
            if (val === null) return null;
            const prev = i === 0 ? val : hourlyCumulative[i - 1];
            if (prev === null) return 0;
            return Math.max(0, Number((val - prev).toFixed(2)));
        });

        return hourlyConsumption.map((consumption, hour) => {
            const isDay = hour >= DAY_START_HOUR && hour < DAY_END_HOUR;
            return {
                hour: `${String(hour).padStart(2, '0')}:00`,
                Day: isDay ? consumption : null,
                Night: !isDay ? consumption : null
            };
        });
    };

    const fetchGraphData = async () => {
        try {
            setLoading(true);
            const baseEndpoint = '/api/app/power_meter_management/controllers/history_data';
            const dateStr = formatApiDate(selectedDate);
            const endpoint = `${baseEndpoint}/meter_single_day_consumption/${meterId}?start_date=${dateStr}&end_date=${dateStr}`;

            const response = await makeRequest2(endpoint, 'GET');

            const rawReadings = response?.success
                ? response.data.data || []
                : response?.data?.data || response?.data || [];

            setGraphData(buildHourlySeries(Array.isArray(rawReadings) ? rawReadings : [], selectedDate));
        } catch (error) {
            console.error('Error fetching consumption graph data:', error);
            toastify('An error occurred while fetching consumption graph data', 'error');
            setGraphData([]);
        } finally {
            setLoading(false);
        }
    };

    const hasData = graphData.some((d) => d.Day !== null || d.Night !== null);

    return (
        <div className="tab-pane show active" role="tabpanel">
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="row">
                                <div className="col-md-4 col-xs-8 mt-2">
                                    <label>Day</label>
                                    <Calendar
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.value)}
                                        dateFormat="yy-mm-dd"
                                        maxDate={new Date()}
                                        className="w-100"
                                        showIcon
                                    />
                                </div>
                                <div className="col-md-3 col-xs-4 mt-2">
                                    <label>&nbsp;</label>
                                    <Button
                                        label="Filter"
                                        className="form-control"
                                        onClick={fetchGraphData}
                                        loading={loading}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            {!hasData && !loading ? (
                                <p className="text-center text-muted mb-0">No consumption data found for the selected day.</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={400}>
                                    <LineChart data={graphData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="hour" />
                                        <YAxis label={{ value: 'kWh', angle: -90, position: 'insideLeft' }} />
                                        <Tooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="Day"
                                            name="Day (06:00–18:00)"
                                            stroke="#f5a623"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                            connectNulls
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="Night"
                                            name="Night (18:00–06:00)"
                                            stroke="#2c3e50"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                            connectNulls
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsumptionGraphTab;